import React, { Suspense, useEffect, useState } from "react";

import { useLocation } from "react-router-dom";

import {
  BadgeCategories,
  BadgeGroup,
  BadgeProps,
  Spinner,
} from "@at/design-system-elements";
import { ATDSStyledContainer, DisplayType } from "@at/design-system-foundation";

import { ATDSBreadcrumbs } from "@at/design-system-patterns";
import { RedirectRoute } from "@at/sauron-platform-core";

import { PageData } from "@at/sauron-platform-core/lib/page/page-data";

import loadable from "@loadable/component";

import * as Styles from "./leasing-product-page.styles";
import { Channel } from "../../__generated__/fed-graphql-types";
import { HowLongToCharge } from "../../components/advert/electric-charging/how-long-to-charge";
import HowOftenModal from "../../components/advert/electric-charging/modals/how-often.modal";
import { LeasingBanner } from "../../components/leasing/banner/leasing-banner";
import { useLeasingQueries } from "../../components/leasing/contract-customiser/hooks/useLeasingQueries/use-leasing-queries.hook";
import { LeasingContractCustomiser } from "../../components/leasing/contract-customiser/leasing-contract-customiser";
import { LeasingGallery } from "../../components/leasing/gallery/leasing-gallery";

import { LeasingModalHub } from "../../components/leasing/modal-hub/leasing-modal-hub";
import { LeasingTermsAndConditions } from "../../components/leasing/terms-and-conditions/leasing-terms-and-conditions";

import { FPADataQuery_search_Search_advert_Advert_specification_Specification_optionalFeatures_Feature } from "../../connected-components/hoc/query/__generated__/fpa-page-data.query";
import { LeasingVehicleSummaryList } from "../../connected-components/leasing-vehicle-summary-list/leasing-vehicle-summary-list";
import { useLeasingStateContext } from "../../contexts/leasing/context/leasing.context";
import { useChannel } from "../../utils/hooks/use-channel";

import { usePageData } from "../../utils/hooks/use-page-data";
import { getBreadcrumbsData } from "../../utils/leasing/getBreadcrumbsData/get-breadcrumbs-data";
import { useTracking } from "../../utils/tracking/use-tracking";
import { SkeletonExpertReview } from "../skeleton/skeleton-expert-review";
import { SkeletonYouMayAlsoLike } from "../skeleton/skeleton-you-may-also-like";

const LazyLeasingStrip = loadable(
  () =>
    import(
      "../../components/leasing/leasing-vehicle-strip/leasing-vehicle-strip.component"
    ),
);

const LazyExpertReview = loadable(
  () =>
    import(
      /* webpackChunkName: "LazyExpertReview" */
      "../../components/leasing/expert-review/leasing-expert-review"
    ),
);

export const LeasingProductPageContent = () => {
  const state = useLeasingStateContext();
  const channel = useChannel();
  const { search } = useLocation();
  const { trackPageViewEvent } = useTracking({
    pageName: `${channel}:leasing:product`,
  });
  const [hasFiredPageView, setHasFiredPageView] = useState(false);
  const hasQueryParams = search.includes("?");

  const { productData } = state;

  const {
    productPageData,
    productPageLoading,
    productPageError,
    leaseOffersLoading,
  } = useLeasingQueries();

  useEffect(() => {
    if (!hasFiredPageView) {
      trackPageViewEvent();
      setHasFiredPageView(true);
    }
  }, [trackPageViewEvent, hasFiredPageView]);

  const advert = productData ?? productPageData?.search.advert;
  const vehicleMake = advert?.specification?.make || "";
  const vehicleModel = advert?.specification?.model || "";
  const advertGenerationId = advert?.generation?.generationId;
  const advertPaintColour = advert?.manufacturerColour;
  const advertTrim = advert?.specification.trim;
  const derivative = advert?.specification?.derivative || "";
  const images = advert?.imageList?.images ?? [];
  const bodyType = advert?.generation?.bodyType || "";
  const fuelType = advert?.specification?.fuel || "";
  const { metaTitle, metaDescription, canonical } = usePageData(
    advert,
    channel,
  );

  const optionalFeatures: BadgeProps[] | undefined =
    advert?.specification.optionalFeatures?.map(
      (
        feature: FPADataQuery_search_Search_advert_Advert_specification_Specification_optionalFeatures_Feature | null,
      ) => {
        return {
          text: feature?.description || "",
          category: BadgeCategories.default,
        };
      },
    );

  if (productPageLoading) return <Spinner />;

  if (productPageError || !advert) {
    return (
      <RedirectRoute
        to={{ pathname: `/${channel}/leasing` }}
        statusCode={301}
      />
    );
  }

  return (
    <>
      <PageData
        title={metaTitle}
        metaDescription={metaDescription}
        canonical={canonical}
        meta={hasQueryParams ? [{ name: "robots", content: "noindex" }] : []}
      />
      <LeasingBanner />
      <Styles.LeasingBreadcrumbContainer>
        <ATDSBreadcrumbs
          pathSegments={getBreadcrumbsData({
            channel,
            derivative,
            vehicleMake,
            vehicleModel,
          })}
          lastChildEllipsis={true}
        />
      </Styles.LeasingBreadcrumbContainer>
      <Styles.ChatLauncher />
      <ATDSStyledContainer displayType={DisplayType.CONSTRAINED}>
        <Styles.LeasingProductMobileHeading>
          <BadgeGroup
            content={[
              { text: "Brand new", category: BadgeCategories.default },
              { text: "Lease deal", category: BadgeCategories.default },
            ]}
          />
          <Styles.LeasingProductTitle as="h2" weight="500">
            {advert.specification.make} {advert.specification.model}{" "}
            {advert.specification.derivative && (
              <Styles.LeasingProductDerivative>
                {advert.specification.derivative}
              </Styles.LeasingProductDerivative>
            )}
          </Styles.LeasingProductTitle>
          {optionalFeatures && (
            <Styles.LeasingOptionalFeatures content={optionalFeatures} />
          )}
        </Styles.LeasingProductMobileHeading>
        <Styles.LeasingProductGrid>
          <Styles.LeasingProductGridLeft>
            <LeasingGallery
              generationId={advertGenerationId ?? ""}
              paintColour={advertPaintColour ?? undefined}
              trim={advertTrim ?? undefined}
              make={vehicleMake}
              advertImages={images}
            />
            <Styles.LeasingProductDisclosure as="span">
              Images are for illustration purposes only
            </Styles.LeasingProductDisclosure>
          </Styles.LeasingProductGridLeft>
          <Styles.LeasingProductGridRight>
            <Styles.LeasingProductDesktopHeading>
              <BadgeGroup
                content={[
                  { text: "Brand new", category: BadgeCategories.default },
                  { text: "Lease deal", category: BadgeCategories.default },
                ]}
              />
              <Styles.LeasingProductTitle as="h1" weight="500">
                {advert.specification.make} {advert.specification.model}{" "}
                {advert.specification.derivative && (
                  <Styles.LeasingProductDerivative>
                    {advert.specification.derivative}
                  </Styles.LeasingProductDerivative>
                )}
              </Styles.LeasingProductTitle>
              {optionalFeatures && (
                <Styles.LeasingOptionalFeatures content={optionalFeatures} />
              )}
            </Styles.LeasingProductDesktopHeading>
            <LeasingContractCustomiser
              leaseOffersLoading={leaseOffersLoading}
            />
          </Styles.LeasingProductGridRight>
        </Styles.LeasingProductGrid>
      </ATDSStyledContainer>
      <ATDSStyledContainer displayType={DisplayType.CONSTRAINED}>
        <LeasingVehicleSummaryList />
        {fuelType === "Electric" && (
          <Styles.LeasingChargingTimes>
            <HowLongToCharge
              fulWidthStyles={true}
              chargingData={advert.specification?.techData?.chargingData}
            />
            <HowOftenModal
              chargingData={advert.specification?.techData?.chargingData}
            />
          </Styles.LeasingChargingTimes>
        )}
      </ATDSStyledContainer>
      <ATDSStyledContainer displayType={DisplayType.CONSTRAINED}>
        <Suspense fallback={<SkeletonExpertReview />}>
          <LazyExpertReview
            make={vehicleMake}
            model={vehicleModel}
            bodyType={bodyType}
          />
        </Suspense>
      </ATDSStyledContainer>
      {channel === Channel.cars && (
        <Suspense fallback={<SkeletonYouMayAlsoLike />}>
          <LazyLeasingStrip />
        </Suspense>
      )}
      <LeasingTermsAndConditions />
      <LeasingModalHub />
    </>
  );
};
